import { Component, inject, ChangeDetectionStrategy } from '@angular/core';

import { MatCardModule } from '@angular/material/card';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { MatInput } from '@angular/material/input';
import { GalaxyUploaderModule } from '@vendasta/galaxy/uploader';
import { SmsDocumentUploaderComponent } from '../document-uploader/sms-document-uploader.component';

import { MatButton } from '@angular/material/button';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { SmsAddressFieldsComponent } from '../address-fields/sms-address-fields.component';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { Attribute, CreateRegulationInfoRequest, RegulationApiService, SupportingDocumentInfo } from '@vendasta/smsv2';
import { SmsContextToken } from '../../../../token';
import { SmsContext } from '../../../../interface';
import { map, take } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import {
  DOCUMENT_ID_CONTROL,
  DOCUMENT_TYPE_CONTROL,
  PROOF_DOCUMENT_CONTROL,
} from '../document-uploader/sms-document-uploader.component';
import { TranslateModule } from '@ngx-translate/core';
import { SMSI18NModule } from '../../../../assets/i18n/sms-i18n.module';
import {
  BUSINESS_NAME_FOR_PROCESSING_ADDRESS,
  CITY_FOR_PROCESSING_ADDRESS,
  COUNTRY_FOR_PROCESSING_ADDRESS,
  POSTAL_CODE_FOR_PROCESSING_ADDRESS,
  STATE_FOR_PROCESSING_ADDRESS,
  STREET_ADDRESS_FOR_PROCESSING_ADDRESS,
  STREET_ADDRESS_SECONDARY_FOR_PROCESSING_ADDRESS,
} from '../sms-regulatory-form.component';

const SOUTH_AFRICA_COUNTRY_CODE = 'ZA';

@Component({
  selector: 'sms-south-africa-form',
  imports: [
    MatCardModule,
    GalaxyFormFieldModule,
    MatInput,
    GalaxyUploaderModule,
    SmsDocumentUploaderComponent,
    MatButton,
    GalaxyAlertModule,
    SmsAddressFieldsComponent,
    ReactiveFormsModule,
    TranslateModule,
    SMSI18NModule
],
  templateUrl: './sms-south-africa-form.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './sms-south-africa-form.component.scss',
})
export class SmsSouthAfricaFormComponent {
  private readonly snackbarService = inject(SnackbarService);
  private readonly regulationApiService = inject(RegulationApiService);
  private readonly context = inject<SmsContext>(SmsContextToken);

  businessIdentityFormGroup = new FormGroup({
    businessName: new FormControl('', { nonNullable: true, validators: Validators.required }),
    businessRegistrationNumber: new FormControl('', { nonNullable: true, validators: Validators.required }),
  });

  businessAddressFormGroup = new FormGroup({});

  contactFormGroup = new FormGroup({
    contactFirstName: new FormControl('', { nonNullable: true, validators: Validators.required }),
    contactLastName: new FormControl('', { nonNullable: true, validators: Validators.required }),
    contactIdentityNumber: new FormControl('', { nonNullable: true, validators: Validators.required }),
    contactNationality: new FormControl('', { nonNullable: true, validators: Validators.required }),
  });

  formGroup = new FormGroup({
    businessIdentity: this.businessIdentityFormGroup,
    businessAddress: this.businessAddressFormGroup,
    contact: this.contactFormGroup,
  });

  submitRegulatoryForm() {
    if (this.formGroup.invalid) {
      this.formGroup.markAllAsTouched();
      this.snackbarService.openErrorSnack('Please fill out all required fields');
      return;
    }

    this.context.ownerId$
      .pipe(
        take(1),
        map((ownerId) => this.convertFormToRequest(ownerId)),
        switchMap((req) => this.regulationApiService.createRegulationInfo(req)),
      )
      .subscribe({
        next: () => {
          this.snackbarService.openSuccessSnack('Regulatory information submitted');
        },
        error: () => {
          this.snackbarService.openErrorSnack('Failed to submit regulatory information');
        },
      });
  }

  private convertFormToRequest(ownerID: string): CreateRegulationInfoRequest {
    let req = new CreateRegulationInfoRequest();
    req.countryCode = SOUTH_AFRICA_COUNTRY_CODE;
    req.ownerId = ownerID;
    req.ownerType = this.context.ownerType;
    req.supportingDocuments = [];

    req = this.addAttributesToRequest(req);
    req = this.addBusinessIdentitySupportingDocument(req);
    req = this.addBusinessAddressSupportingDocument(req);
    req = this.addContactSupportingDocument(req);

    return req;
  }

  private addAttributesToRequest(req: CreateRegulationInfoRequest): CreateRegulationInfoRequest {
    const businessNameAttribute = new Attribute({
      key: 'business_name',
      value: this.businessIdentityFormGroup.controls.businessName.value || '',
    });
    const firstNameAttribute = new Attribute({
      key: 'first_name',
      value: this.contactFormGroup.controls.contactFirstName.value || '',
    });
    const lastNameAttribute = new Attribute({
      key: 'last_name',
      value: this.contactFormGroup.controls.contactLastName.value || '',
    });
    const nationalityAttribute = new Attribute({
      key: 'nationality',
      value: this.contactFormGroup.controls.contactNationality.value || '',
    });
    req.businessAttributes = [businessNameAttribute, firstNameAttribute, lastNameAttribute, nationalityAttribute];

    return req;
  }

  private addBusinessIdentitySupportingDocument(req: CreateRegulationInfoRequest): CreateRegulationInfoRequest {
    const supportingDocumentInfo = new SupportingDocumentInfo();
    supportingDocumentInfo.documentType =
      this.businessIdentityFormGroup.get(PROOF_DOCUMENT_CONTROL)?.get(DOCUMENT_TYPE_CONTROL)?.value || '';
    supportingDocumentInfo.fileId = this.businessIdentityFormGroup.get(PROOF_DOCUMENT_CONTROL)?.get(DOCUMENT_ID_CONTROL)?.value || '';

    // You may see Twilio list these as separate supporting documents in some places
    // They expect only 1 supporting document per type of document
    // So we need to combine them into 1 supporting document
    const businessNameAttribute = new Attribute({
      key: 'business_name',
      value: this.businessIdentityFormGroup.controls.businessName.value || '',
    });
    const businessRegistrationNumberAttribute = new Attribute({
      key: 'document_number',
      value: this.businessIdentityFormGroup.controls.businessRegistrationNumber.value || '',
    });

    supportingDocumentInfo.attributes = [businessNameAttribute, businessRegistrationNumberAttribute];
    req.supportingDocuments.push(supportingDocumentInfo);

    return req;
  }

  private addBusinessAddressSupportingDocument(req: CreateRegulationInfoRequest): CreateRegulationInfoRequest {
    const supportingDocumentInfo = new SupportingDocumentInfo();
    supportingDocumentInfo.documentType =
      this.businessAddressFormGroup.get(PROOF_DOCUMENT_CONTROL)?.get(DOCUMENT_TYPE_CONTROL)?.value || '';
    supportingDocumentInfo.fileId = this.businessAddressFormGroup.get(PROOF_DOCUMENT_CONTROL)?.get(DOCUMENT_ID_CONTROL)?.value || '';

    // Special keys are used because this address needs to be processed in the backend
    supportingDocumentInfo.processAddress = true;
    const businessNameForAddressAttribute = new Attribute({
      key: BUSINESS_NAME_FOR_PROCESSING_ADDRESS,
      value: this.businessIdentityFormGroup.controls.businessName.value || '',
    });
    const streetAddressLine1Attribute = new Attribute({
      key: STREET_ADDRESS_FOR_PROCESSING_ADDRESS,
      value: this.businessAddressFormGroup.get('address')?.get('streetAddress1')?.value || '',
    });
    const cityAttribute = new Attribute({
      key: CITY_FOR_PROCESSING_ADDRESS,
      value: this.businessAddressFormGroup.get('address')?.get('city')?.value || '',
    });
    const stateAttribute = new Attribute({
      key: STATE_FOR_PROCESSING_ADDRESS,
      value: this.businessAddressFormGroup.get('address')?.get('state')?.value || '',
    });
    const postalCodeAttribute = new Attribute({
      key: POSTAL_CODE_FOR_PROCESSING_ADDRESS,
      value: this.businessAddressFormGroup.get('address')?.get('postalCode')?.value || '',
    });
    const countryAttribute = new Attribute({
      key: COUNTRY_FOR_PROCESSING_ADDRESS,
      value: SOUTH_AFRICA_COUNTRY_CODE,
    });
    supportingDocumentInfo.attributes = [
      businessNameForAddressAttribute,
      streetAddressLine1Attribute,
      cityAttribute,
      stateAttribute,
      postalCodeAttribute,
      countryAttribute,
    ];

    const streetAddressLine2Value = this.businessAddressFormGroup.get('address')?.get('streetAddress2')?.value || '';
    if (streetAddressLine2Value) {
      const streetAddressLine2Attribute = new Attribute({
        key: STREET_ADDRESS_SECONDARY_FOR_PROCESSING_ADDRESS,
        value: streetAddressLine2Value,
      });
      supportingDocumentInfo.attributes.push(streetAddressLine2Attribute);
    }

    req.supportingDocuments.push(supportingDocumentInfo);

    return req;
  }

  private addContactSupportingDocument(req: CreateRegulationInfoRequest): CreateRegulationInfoRequest {
    const supportingDocumentInfo = new SupportingDocumentInfo();
    supportingDocumentInfo.documentType = this.contactFormGroup.get(PROOF_DOCUMENT_CONTROL)?.get(DOCUMENT_TYPE_CONTROL)?.value || '';
    supportingDocumentInfo.fileId = this.contactFormGroup.get(PROOF_DOCUMENT_CONTROL)?.get(DOCUMENT_ID_CONTROL)?.value || '';

    const contactFirstNameAttribute = new Attribute({
      key: 'first_name',
      value: this.contactFormGroup.controls.contactFirstName.value || '',
    });
    const contactLastNameAttribute = new Attribute({
      key: 'last_name',
      value: this.contactFormGroup.controls.contactLastName.value || '',
    });
    const contactIdentityNumberAttribute = new Attribute({
      key: 'document_number',
      value: this.contactFormGroup.controls.contactIdentityNumber.value || '',
    });
    const contactNationalityAttribute = new Attribute({
      key: 'nationality',
      value: this.contactFormGroup.controls.contactNationality.value || '',
    });

    supportingDocumentInfo.attributes = [
      contactFirstNameAttribute,
      contactLastNameAttribute,
      contactIdentityNumberAttribute,
      contactNationalityAttribute,
    ];

    supportingDocumentInfo.processAddress = true;
    const contactNameForAddressAttribute = new Attribute({
      key: BUSINESS_NAME_FOR_PROCESSING_ADDRESS,
      value: contactFirstNameAttribute.value + ' ' + contactLastNameAttribute.value,
    });
    const contactStreetAddressLine1Attribute = new Attribute({
      key: STREET_ADDRESS_FOR_PROCESSING_ADDRESS,
      value: this.contactFormGroup.get('address')?.get('streetAddress1')?.value || '',
    });
    const contactCityAttribute = new Attribute({
      key: CITY_FOR_PROCESSING_ADDRESS,
      value: this.contactFormGroup.get('address')?.get('city')?.value || '',
    });
    const contactStateAttribute = new Attribute({
      key: STATE_FOR_PROCESSING_ADDRESS,
      value: this.contactFormGroup.get('address')?.get('state')?.value || '',
    });
    const contactPostalCodeAttribute = new Attribute({
      key: POSTAL_CODE_FOR_PROCESSING_ADDRESS,
      value: this.contactFormGroup.get('address')?.get('postalCode')?.value || '',
    });
    const contactCountryAttribute = new Attribute({
      key: COUNTRY_FOR_PROCESSING_ADDRESS,
      value: SOUTH_AFRICA_COUNTRY_CODE,
    });
    supportingDocumentInfo.attributes.push(
      contactNameForAddressAttribute,
      contactStreetAddressLine1Attribute,
      contactCityAttribute,
      contactStateAttribute,
      contactPostalCodeAttribute,
      contactCountryAttribute,
    );

    const contactStreetAddressLine2Value = this.contactFormGroup.get('address')?.get('streetAddress2')?.value || '';
    if (contactStreetAddressLine2Value) {
      const contactStreetAddressLine2Attribute = new Attribute({
        key: STREET_ADDRESS_SECONDARY_FOR_PROCESSING_ADDRESS,
        value: contactStreetAddressLine2Value,
      });
      supportingDocumentInfo.attributes.push(contactStreetAddressLine2Attribute);
    }

    req.supportingDocuments.push(supportingDocumentInfo);

    return req;
  }
}
